import type { MailboxInfo } from "owa-client-types";
import { getAccountKeyForMailboxInfo } from "owa-client-types";

export interface AccountPersistState {
    skipNextClose: boolean;
    skipNextOpen: boolean;
    panelStateByScenario: Map<string, boolean>;
}

const stateByAccount = new Map<string, AccountPersistState>();

export function getOrCreatePersistState(mailboxInfo: MailboxInfo): AccountPersistState {
    const key = getAccountKeyForMailboxInfo(mailboxInfo);
    let entry = stateByAccount.get(key);
    if (!entry) {
        entry = {
            skipNextClose: false,
            skipNextOpen: false,
            panelStateByScenario: new Map(),
        };
        stateByAccount.set(key, entry);
    }
    return entry;
}

export function setSkipNextCloseForWorkloadSwitch(mailboxInfo: MailboxInfo): void {
    getOrCreatePersistState(mailboxInfo).skipNextClose = true;
}

/**
 * Read skipNextOpen without consuming it. Used by panel-mount telemetry to distinguish
 * user-initiated opens (flag unset) from programmatic opens (flag set by boot restore
 * or module-switch reconciliation).
 */
export function peekSkipNextOpenForWorkloadSwitch(mailboxInfo: MailboxInfo): boolean {
    return getOrCreatePersistState(mailboxInfo).skipNextOpen;
}

/**
 * Read skipNextClose without consuming it. Used by panel-unmount telemetry to distinguish
 * user-initiated closes (flag unset) from programmatic closes (flag set by module-switch
 * reconciliation when the target scenario's saved state is closed).
 */
export function peekSkipNextCloseForWorkloadSwitch(mailboxInfo: MailboxInfo): boolean {
    return getOrCreatePersistState(mailboxInfo).skipNextClose;
}

/**
 * Read all scenario panel-state cache entries for an account. Used by session-snapshot
 * telemetry to capture the per-module open/closed state at session end.
 */
export function getAllScenarioPanelStates(mailboxInfo: MailboxInfo): Map<string, boolean> {
    return new Map(getOrCreatePersistState(mailboxInfo).panelStateByScenario);
}

/**
 * Map a Module value (which may be a deep-link variant) to the canonical scenario key
 * used as a cache key in panelStateByScenario. Cache writes use scenario keys
 * (`Mail` / `Calendar` / `People` / `FilesHub`) but Module values can include deep-link
 * variants (`MailDeepLink`, `CalendarDeepLink`, etc.) — without this mapping, perf-datapoint
 * lookups during a deep-link session would always miss and undercount restored-state opens.
 * Returns undefined for unsupported modules (Tasks, AppHost, etc.).
 */
export function getPersistenceScenarioForModule(module: string | undefined): string | undefined {
    if (!module) {
        return undefined;
    }
    switch (module) {
        case "Mail":
        case "MailDeepLink":
            return "Mail";
        case "Calendar":
        case "CalendarDeepLink":
        case "PublishedCalendar":
            return "Calendar";
        case "People":
        case "PeopleNew":
            return "People";
        case "FilesHub":
            return "FilesHub";
        default:
            return undefined;
    }
}
